import { useCallback, useEffect, useState } from 'react'

export interface MediaDeviceInfo {
  deviceId: string
  label: string
  kind: 'videoinput' | 'audioinput'
}

interface UseMediaDevicesReturn {
  cameraDevices: MediaDeviceInfo[]
  microphoneDevices: MediaDeviceInfo[]
  isLoading: boolean
}

export const useMediaDevices = (): UseMediaDevicesReturn => {
  const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([])
  const [microphoneDevices, setMicrophoneDevices] = useState<MediaDeviceInfo[]>(
    [],
  )
  const [isLoading, setIsLoading] = useState(true)

  const getDevices = useCallback(async () => {
    try {
      setIsLoading(true)

      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        throw new Error('MediaDevices API is not supported')
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      })
      stream.getTracks().forEach(track => track.stop())

      const devices = await navigator.mediaDevices.enumerateDevices()

      const cameras = devices
        .filter(
          device => device.kind === 'videoinput' && device.deviceId !== '',
        )
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `カメラ ${device.deviceId.slice(0, 8)}`,
          kind: 'videoinput' as const,
        }))

      const microphones = devices
        .filter(
          device => device.kind === 'audioinput' && device.deviceId !== '',
        )
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `マイク ${device.deviceId.slice(0, 8)}`,
          kind: 'audioinput' as const,
        }))

      setCameraDevices(cameras)
      setMicrophoneDevices(microphones)
    } catch (err) {
      console.error('Failed to get media devices:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    getDevices()

    const handleDeviceChange = () => {
      getDevices()
    }

    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange)
    return () => {
      navigator.mediaDevices?.removeEventListener(
        'devicechange',
        handleDeviceChange,
      )
    }
  }, [getDevices])

  return {
    cameraDevices,
    microphoneDevices,
    isLoading,
  }
}
